import { CheckIcon, CopyIcon } from '@radix-ui/react-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type CopyValue = string | (() => string | Promise<string>);
export type CopyStatus = 'idle' | 'copied' | 'failed';

const FEEDBACK: Record<CopyStatus, string> = { idle: '', copied: 'Copied', failed: "Couldn't copy" };

/** Clipboard write with a status that resets after `resetMs`; `feedback` is for a live region. */
export function useCopyToClipboard(resetMs = 2000) {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(
    async (value: CopyValue) => {
      clearTimeout(timer.current);
      try {
        const text = typeof value === 'function' ? await value() : value;
        await navigator.clipboard.writeText(text);
        setStatus('copied');
      } catch {
        // No clipboard access (insecure context, denied permission) or the value failed to load.
        setStatus('failed');
      }
      timer.current = setTimeout(() => setStatus('idle'), resetMs);
    },
    [resetMs],
  );

  return { status, feedback: FEEDBACK[status], copy };
}

/** Icon button that copies a value (or the result of a loader) and confirms for screen readers too. */
export function CopyButton({
  value,
  label = 'Copy',
  className,
}: {
  value: CopyValue;
  label?: string;
  className?: string;
}) {
  const { status, feedback, copy } = useCopyToClipboard();
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            className={cn('text-muted-foreground', className)}
            onClick={() => void copy(value)}
          >
            {status === 'copied' ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{feedback || label}</TooltipContent>
      </Tooltip>
      <span role="status" className="sr-only">
        {feedback}
      </span>
    </>
  );
}
